import { CommonModule } from '@angular/common';
import { Component, OnInit, Optional, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { EditorModule } from 'primeng/editor';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { FieldsetModule } from 'primeng/fieldset';
import { ImageModule } from 'primeng/image';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { finalize, forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { Length } from '../../models/meta-params/length';
import { BaseMasterDataService } from '../../services/base-master-data.service';
import { FirebaseStorageService } from '../../services/firebase-storage.service';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { TRACKING_MODES, WarehousingMasterType, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingItemImage, WarehousingItemMeasurementMode, WarehousingItemUomConversion, WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-item-form',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, CardModule, CheckboxModule,
    DialogModule, EditorModule, FieldsetModule, FileUploadModule, ImageModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule,
    PageFormSectionComponent, PageHeroComponent, PageShellComponent, ProgressBarModule, SelectModule, SelectButtonModule, ToastModule
  ],
  providers: [MessageService],
  templateUrl: './warehousing-item-form.component.html'
})
export class WarehousingItemFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private baseMasterDataService = inject(BaseMasterDataService);
  private firebaseStorageService = inject(FirebaseStorageService);
  constructor(
    @Optional() private dialogRef: DynamicDialogRef | null,
    @Optional() private dialogConfig: DynamicDialogConfig | null
  ) {}

  isLoading = false;
  isSaving = false;
  isEditMode = false;
  isDialogMode = false;
  isEmbeddedMode = false;
  itemId?: number;
  returnEvent = 'WAREHOUSING_ITEM_CREATED';
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('New Item', 'Items', '/warehousing/items');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  lengthUnits: Length[] = [];
  uploadedImages: WarehousingItemImage[] = [];
  imageUploadProgress = 0;
  imageUploadInProgress = false;
  masterData = masterDataMap();
  trackingModes = TRACKING_MODES;
  measurementModes: { label: string; value: WarehousingItemMeasurementMode }[] = [
    { label: 'Measure by dimensions', value: 'DIMENSIONS' },
    { label: 'Enter SQM / CBM directly', value: 'DIRECT' }
  ];
  setupDialogVisible = false;
  setupDialogType: WarehousingMasterType = 'CATEGORY';
  setupDialogSaving = false;

  itemForm = this.fb.group({
    crmClientId: [null as number | null, Validators.required],
    crmAccountId: [null as number | null, Validators.required],
    itemNumber: [{ value: '', disabled: true }],
    itemBarcode: [''],
    customerItemNumber: [''],
    itemName: ['', Validators.required],
    itemDescription: [''],
    skuNumber: [''],
    tenantUomId: [null as number | null],
    packUomId: [null as number | null],
    packFactor: [null as number | null, Validators.min(0.0001)],
    caseUomId: [null as number | null],
    caseFactor: [null as number | null, Validators.min(0.0001)],
    palletUomId: [null as number | null],
    palletFactor: [null as number | null, Validators.min(0.0001)],
    categoryId: [null as number | null],
    brandId: [null as number | null],
    subBrandId: [null as number | null],
    measurementMode: ['DIMENSIONS' as WarehousingItemMeasurementMode],
    lengthUnitId: [null as number | null, Validators.required],
    lengthMeters: [0, [Validators.required, Validators.min(0)]],
    breadthMeters: [0, [Validators.required, Validators.min(0)]],
    heightMeters: [0, [Validators.required, Validators.min(0)]],
    directSqm: [null as number | null, Validators.min(0)],
    directCbm: [null as number | null, Validators.min(0)],
    weightKg: [null as number | null],
    trackingMode: ['QUANTITY', Validators.required],
    isActive: [true]
  });

  setupForm = this.fb.group({
    name: ['', Validators.required],
    code: [''],
    parentId: [null as number | null],
    categoryId: [null as number | null],
    brandId: [null as number | null],
    isActive: [true]
  });

  ngOnInit(): void {
    this.isDialogMode = !!this.dialogRef;
    this.itemId = Number(this.dialogConfig?.data?.itemId ?? this.route.snapshot.paramMap.get('id')) || undefined;
    this.isEditMode = !!this.itemId;
    this.isEmbeddedMode = this.route.snapshot.queryParamMap.get('embedded') === '1';
    this.returnEvent = this.route.snapshot.queryParamMap.get('returnEvent') || this.returnEvent;
    this.breadcrumbItems = warehousingBreadcrumb(this.isEditMode ? 'Edit Item' : 'New Item', 'Items', '/warehousing/items');
    this.registerCascadeResets();
    this.registerMeasurementModeToggle();
    this.loadInitialData();
  }

  loadInitialData(): void {
    this.isLoading = true;
    forkJoin({
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      items: this.warehousingService.getItems(),
      categories: this.warehousingService.getMasterData('CATEGORY'),
      brands: this.warehousingService.getMasterData('BRAND'),
      subBrands: this.warehousingService.getMasterData('SUB_BRAND'),
      tenantUoms: this.warehousingService.getMasterData('TENANT_UOM'),
      lengths: this.baseMasterDataService.getLengths('en-US')
    }).subscribe({
      next: response => {
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.lengthUnits = response.lengths || [];
        this.masterData.CATEGORY = response.categories.data || [];
        this.masterData.BRAND = response.brands.data || [];
        this.masterData.SUB_BRAND = response.subBrands.data || [];
        this.masterData.TENANT_UOM = response.tenantUoms.data || [];
        if (this.itemId) {
          const item = (response.items.data || []).find(row => row.id === this.itemId);
        if (item) this.patchItem(item);
        } else {
          this.itemForm.patchValue({ lengthUnitId: this.defaultLengthUnitId() }, { emitEvent: false });
          this.patchEmbeddedDefaults();
        }
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  saveItem(): void {
    if (this.isSaving || this.imageUploadInProgress) {
      if (this.isSaving) {
        return;
      }
      this.messageService.add({ severity: 'warn', summary: 'Upload in Progress', detail: 'Wait for item images to finish uploading before saving.' });
      return;
    }
    if (this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }

    const value = this.itemForm.getRawValue();
    const isDirect = value.measurementMode === 'DIRECT';
    if (isDirect && !(value.directSqm && value.directSqm > 0) && !(value.directCbm && value.directCbm > 0)) {
      this.itemForm.get('directSqm')?.markAsTouched();
      this.itemForm.get('directCbm')?.markAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'SQM or CBM Required', detail: 'Enter SQM or CBM when measuring directly.' });
      return;
    }
    const uomConversions = this.uomConversions(value);
    if (!uomConversions) {
      this.messageService.add({ severity: 'warn', summary: 'UOM conversion incomplete', detail: 'Provide both a UOM and a positive conversion factor for Pack, Case, or Pallet.' });
      return;
    }
    const conversionIds = uomConversions.map(conversion => conversion.uomId);
    if (conversionIds.includes(value.tenantUomId!) || new Set(conversionIds).size !== conversionIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'Distinct UOMs required',
        detail: 'Pack, Case, and Pallet UOMs must be distinct from the base Tenant UOM and from one another.' });
      return;
    }
    const payload: WarehousingItem = {
      id: this.itemId,
      crmClientId: value.crmClientId!,
      crmAccountId: value.crmAccountId!,
      itemBarcode: value.itemBarcode || undefined,
      customerItemNumber: value.customerItemNumber || undefined,
      itemName: value.itemName!,
      itemDescription: value.itemDescription || undefined,
      skuNumber: value.skuNumber || undefined,
      tenantUomId: value.tenantUomId || undefined,
      baseUomId: value.tenantUomId || undefined,
      uomConversions,
      categoryId: value.categoryId || undefined,
      brandId: value.brandId || undefined,
      subBrandId: value.subBrandId || undefined,
      measurementMode: isDirect ? 'DIRECT' : 'DIMENSIONS',
      lengthUnitId: isDirect ? undefined : value.lengthUnitId!,
      lengthMeters: isDirect ? undefined : value.lengthMeters || 0,
      breadthMeters: isDirect ? undefined : value.breadthMeters || 0,
      heightMeters: isDirect ? undefined : value.heightMeters || 0,
      sqm: isDirect ? value.directSqm || 0 : undefined,
      cbm: isDirect ? value.directCbm || 0 : undefined,
      weightKg: value.weightKg || undefined,
      trackingMode: value.trackingMode as 'QUANTITY' | 'UNIT',
      isActive: !!value.isActive,
      images: this.uploadedImages.map((image, index) => ({
        ...image,
        sortOrder: index,
        primaryImage: index === 0
      }))
    };

    this.isSaving = true;
    const request = this.isEditMode ? this.warehousingService.updateItem(payload) : this.warehousingService.createItem(payload);
    request.pipe(finalize(() => this.isSaving = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Item Saved', detail: 'Warehousing item was saved.' });
      if (this.isDialogMode && this.dialogRef) {
        this.dialogRef.close(resp.data || payload);
        return;
      }
      if (this.isEmbeddedMode) {
        this.postEmbeddedMessage(this.returnEvent, resp.data || payload);
        return;
      }
      this.router.navigate(['/warehousing/items/view', resp.data?.id || this.itemId]);
    });
  }

  discard(): void {
    if (this.isDialogMode && this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    if (this.isEmbeddedMode) {
      this.postEmbeddedMessage('WAREHOUSING_EMBEDDED_CLOSE', { returnEvent: this.returnEvent });
      return;
    }
    this.router.navigate(['/warehousing/items']);
  }

  uploadImages(event: FileUploadHandlerEvent): void {
    const files = event.files || [];
    if (!files.length) {
      return;
    }
    const invalidFile = files.find(file => !file.type.startsWith('image/'));
    if (invalidFile) {
      this.messageService.add({ severity: 'warn', summary: 'Image Required', detail: `${invalidFile.name} is not an image file.` });
      return;
    }
    this.imageUploadInProgress = true;
    this.uploadNextImage(files, 0);
  }

  removeImage(image: WarehousingItemImage): void {
    this.uploadedImages = this.uploadedImages.filter(row => row !== image);
  }

  filteredAccounts(clientId: number | null | undefined): CrmAccount[] {
    return this.accounts.filter(account => !clientId || !account.crmClientId || account.crmClientId === clientId);
  }

  options(type: WarehousingMasterType): WarehousingMasterData[] {
    return this.masterData[type] || [];
  }

  categoryOptions(): WarehousingMasterData[] {
    const clientId = this.itemForm.value.crmClientId;
    const accountId = this.itemForm.value.crmAccountId;
    const sourceRows = this.masterData.CATEGORY.filter(row =>
      (!row.crmClientId || !clientId || row.crmClientId === clientId) &&
      (!row.crmAccountId || !accountId || row.crmAccountId === accountId)
    );
    return this.categoryTreeOptions(sourceRows);
  }

  brandOptions(): WarehousingMasterData[] {
    const categoryId = this.itemForm.value.categoryId;
    return this.masterData.BRAND.filter(row => !categoryId || row.parentId === categoryId || row.categoryId === categoryId);
  }

  subBrandOptions(): WarehousingMasterData[] {
    const brandId = this.itemForm.value.brandId;
    return this.masterData.SUB_BRAND.filter(row => !brandId || row.parentId === brandId || row.brandId === brandId);
  }

  dimensionUnitLabel(): string {
    const lengthUnitId = this.itemForm.value.lengthUnitId;
    const unit = this.lengthUnits.find(row => row.lengthId === lengthUnitId);
    return unit?.shortName || unit?.fullName || 'Unit';
  }

  openSetupDialog(type: WarehousingMasterType): void {
    this.setupDialogType = type;
    this.setupDialogSaving = false;
    this.setupForm.reset({
      name: '',
      code: '',
      parentId: type === 'CATEGORY' ? this.itemForm.value.categoryId || null : null,
      categoryId: type === 'BRAND' ? this.itemForm.value.categoryId || null : null,
      brandId: type === 'SUB_BRAND' ? this.itemForm.value.brandId || null : null,
      isActive: true
    });
    this.setupDialogVisible = true;
  }

  private patchEmbeddedDefaults(): void {
    if (!this.isEmbeddedMode && !this.isDialogMode) {
      return;
    }
    const crmClientId = Number(this.dialogConfig?.data?.crmClientId ?? this.route.snapshot.queryParamMap.get('crmClientId')) || null;
    const crmAccountId = Number(this.dialogConfig?.data?.crmAccountId ?? this.route.snapshot.queryParamMap.get('crmAccountId')) || null;
    this.itemForm.patchValue({
      crmClientId,
      crmAccountId
    }, { emitEvent: false });
  }

  private postEmbeddedMessage(type: string, payload: unknown): void {
    window.parent?.postMessage({ type, payload }, window.location.origin);
  }

  saveSetupValue(): void {
    if (this.setupDialogSaving) {
      return;
    }
    if (this.setupForm.invalid) {
      this.setupForm.markAllAsTouched();
      return;
    }
    const value = this.setupForm.getRawValue();
    if (this.setupDialogType === 'BRAND' && !value.categoryId) {
      this.setupForm.get('categoryId')?.setErrors({ required: true });
      this.setupForm.markAllAsTouched();
      return;
    }
    if (this.setupDialogType === 'SUB_BRAND' && !value.brandId) {
      this.setupForm.get('brandId')?.setErrors({ required: true });
      this.setupForm.markAllAsTouched();
      return;
    }
    const payload: WarehousingMasterData = {
      type: this.setupDialogType,
      name: value.name!,
      code: value.code || undefined,
      isActive: value.isActive ?? true
    };
    if (this.setupDialogType === 'CATEGORY') {
      payload.crmClientId = this.itemForm.value.crmClientId || undefined;
      payload.crmAccountId = this.itemForm.value.crmAccountId || undefined;
      payload.parentId = value.parentId || undefined;
    }
    if (this.setupDialogType === 'BRAND') {
      payload.categoryId = value.categoryId || undefined;
      payload.parentId = value.categoryId || undefined;
    }
    if (this.setupDialogType === 'SUB_BRAND') {
      payload.brandId = value.brandId || undefined;
      payload.parentId = value.brandId || undefined;
    }

    this.setupDialogSaving = true;
    this.warehousingService.saveMasterData(payload).subscribe({
      next: resp => {
        this.setupDialogSaving = false;
        if (!resp.success || !resp.data) return;
        const saved = resp.data;
        this.masterData[this.setupDialogType] = [
          saved,
          ...this.masterData[this.setupDialogType].filter(row => row.id !== saved.id)
        ];
        if (this.setupDialogType === 'TENANT_UOM') this.itemForm.patchValue({ tenantUomId: saved.id || null });
        if (this.setupDialogType === 'CATEGORY') this.itemForm.patchValue({ categoryId: saved.id || null });
        if (this.setupDialogType === 'BRAND') this.itemForm.patchValue({ brandId: saved.id || null });
        if (this.setupDialogType === 'SUB_BRAND') this.itemForm.patchValue({ subBrandId: saved.id || null });
        this.setupDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Setup Value Added', detail: `${saved.name} was added.` });
      },
      error: err => {
        this.setupDialogSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: err?.error?.message || 'Setup value could not be saved.' });
      }
    });
  }

  setupDialogTitle(): string {
    switch (this.setupDialogType) {
      case 'TENANT_UOM': return 'New Tenant UOM';
      case 'CATEGORY': return 'New Category';
      case 'BRAND': return 'New Brand';
      case 'SUB_BRAND': return 'New Sub Brand';
      default: return 'New Setup Value';
    }
  }

  showSetupParentCategory(): boolean {
    return this.setupDialogType === 'CATEGORY';
  }

  showSetupCategory(): boolean {
    return this.setupDialogType === 'BRAND';
  }

  showSetupBrand(): boolean {
    return this.setupDialogType === 'SUB_BRAND';
  }

  sqmPreview(): number {
    const value = this.itemForm.getRawValue();
    if (value.measurementMode === 'DIRECT') {
      return Number((value.directSqm || 0).toFixed(4));
    }
    const multiplier = this.dimensionMultiplier();
    return Number(((value.lengthMeters || 0) * multiplier * (value.breadthMeters || 0) * multiplier).toFixed(4));
  }

  cbmPreview(): number {
    const value = this.itemForm.getRawValue();
    if (value.measurementMode === 'DIRECT') {
      return Number((value.directCbm || 0).toFixed(4));
    }
    const multiplier = this.dimensionMultiplier();
    return Number(((value.lengthMeters || 0) * multiplier * (value.breadthMeters || 0) * multiplier * (value.heightMeters || 0) * multiplier).toFixed(4));
  }

  private patchItem(item: WarehousingItem): void {
    this.uploadedImages = [...(item.images || [])]
      .sort((left, right) => (left.sortOrder || 0) - (right.sortOrder || 0))
      .map((image, index) => ({ ...image, sortOrder: index, primaryImage: index === 0 }));
    this.itemForm.patchValue({
      crmClientId: item.crmClientId,
      crmAccountId: item.crmAccountId,
      itemNumber: item.itemNumber || '',
      itemBarcode: item.itemBarcode || '',
      customerItemNumber: item.customerItemNumber || '',
      itemName: item.itemName,
      itemDescription: item.itemDescription || '',
      skuNumber: item.skuNumber || '',
      tenantUomId: item.tenantUomId || null,
      packUomId: this.conversion(item, 'PACK')?.uomId || null,
      packFactor: this.conversion(item, 'PACK')?.factorToBase || null,
      caseUomId: this.conversion(item, 'CASE')?.uomId || null,
      caseFactor: this.conversion(item, 'CASE')?.factorToBase || null,
      palletUomId: this.conversion(item, 'PALLET')?.uomId || null,
      palletFactor: this.conversion(item, 'PALLET')?.factorToBase || null,
      categoryId: item.categoryId || null,
      brandId: item.brandId || null,
      subBrandId: item.subBrandId || null,
      measurementMode: item.measurementMode === 'DIRECT' ? 'DIRECT' : 'DIMENSIONS',
      lengthUnitId: item.lengthUnitId || this.defaultLengthUnitId(),
      lengthMeters: item.lengthMeters || 0,
      breadthMeters: item.breadthMeters || 0,
      heightMeters: item.heightMeters || 0,
      directSqm: item.measurementMode === 'DIRECT' ? item.sqm || null : null,
      directCbm: item.measurementMode === 'DIRECT' ? item.cbm || null : null,
      weightKg: item.weightKg || null,
      trackingMode: item.trackingMode === 'UNIQUE_ASSET' || item.isDisplayStand ? 'UNIT' : item.trackingMode,
      isActive: item.isActive ?? true
    });
    if (this.isEditMode) this.itemForm.controls.trackingMode.disable();
  }

  isDirectMeasurement(): boolean {
    return this.itemForm.get('measurementMode')?.value === 'DIRECT';
  }

  private uomConversions(value: ReturnType<typeof this.itemForm.getRawValue>): WarehousingItemUomConversion[] | null {
    const rows = [
      { label: 'PACK', uomId: value.packUomId, factorToBase: value.packFactor },
      { label: 'CASE', uomId: value.caseUomId, factorToBase: value.caseFactor },
      { label: 'PALLET', uomId: value.palletUomId, factorToBase: value.palletFactor }
    ].filter(row => !!row.uomId || !!row.factorToBase);
    if (rows.some(row => !row.uomId || !row.factorToBase || row.factorToBase <= 0)) {
      return null;
    }
    return rows.map(row => {
      return { uomId: row.uomId!, factorToBase: row.factorToBase!, usageType: 'BOTH', packLabel: row.label, active: true };
    });
  }

  private conversion(item: WarehousingItem, label: string): WarehousingItemUomConversion | undefined {
    return (item.uomConversions || []).find(row => row.packLabel?.toUpperCase() === label);
  }

  /**
   * Dimension controls only validate in DIMENSIONS mode; direct SQM/CBM controls only in DIRECT mode.
   * Disabled controls are excluded from form validity, so hidden fields never block saving.
   */
  private registerMeasurementModeToggle(): void {
    const apply = (mode: WarehousingItemMeasurementMode | null | undefined) => {
      const direct = mode === 'DIRECT';
      const setEnabled = (name: string, enabled: boolean) => {
        const control = this.itemForm.get(name);
        if (enabled) {
          control?.enable({ emitEvent: false });
        } else {
          control?.disable({ emitEvent: false });
        }
      };
      ['lengthUnitId', 'lengthMeters', 'breadthMeters', 'heightMeters'].forEach(name => setEnabled(name, !direct));
      ['directSqm', 'directCbm'].forEach(name => setEnabled(name, direct));
    };
    apply(this.itemForm.get('measurementMode')?.value);
    this.itemForm.get('measurementMode')?.valueChanges.subscribe(mode => apply(mode));
  }

  private registerCascadeResets(): void {
    this.itemForm.get('crmClientId')?.valueChanges.subscribe(clientId => {
      const accountId = this.itemForm.value.crmAccountId;
      if (accountId && !this.filteredAccounts(clientId).some(account => account.id === accountId)) {
        this.itemForm.patchValue({ crmAccountId: null }, { emitEvent: false });
      }
      this.resetHierarchy();
    });
    this.itemForm.get('crmAccountId')?.valueChanges.subscribe(() => this.resetHierarchy());
    this.itemForm.get('categoryId')?.valueChanges.subscribe(categoryId => {
      const brandId = this.itemForm.value.brandId;
      if (brandId && !this.brandOptions().some(row => row.id === brandId)) {
        this.itemForm.patchValue({ brandId: null, subBrandId: null }, { emitEvent: false });
      }
    });
    this.itemForm.get('brandId')?.valueChanges.subscribe(brandId => {
      const subBrandId = this.itemForm.value.subBrandId;
      if (subBrandId && !this.subBrandOptions().some(row => row.id === subBrandId)) {
        this.itemForm.patchValue({ subBrandId: null }, { emitEvent: false });
      }
    });
  }

  private resetHierarchy(): void {
    this.itemForm.patchValue({ categoryId: null, brandId: null, subBrandId: null }, { emitEvent: false });
  }

  private defaultLengthUnitId(): number | null {
    const meter = this.lengthUnits.find(unit => {
      const shortName = (unit.shortName || '').trim().toLowerCase();
      const fullName = (unit.fullName || '').trim().toLowerCase();
      return shortName === 'm' || fullName === 'meter' || fullName === 'metre' || fullName === 'meters' || fullName === 'metres';
    });
    return meter?.lengthId || this.lengthUnits[0]?.lengthId || null;
  }

  private dimensionMultiplier(): number {
    const lengthUnitId = this.itemForm.value.lengthUnitId;
    const unit = this.lengthUnits.find(row => row.lengthId === lengthUnitId);
    return unit?.lengthMultiple && unit.lengthMultiple > 0 ? unit.lengthMultiple : 1;
  }

  private categoryTreeOptions(rows: WarehousingMasterData[]): WarehousingMasterData[] {
    const byParent = new Map<number, WarehousingMasterData[]>();
    const ids = new Set(rows.map(row => row.id).filter((id): id is number => !!id));
    rows.forEach(row => {
      if (!row.parentId) return;
      const children = byParent.get(row.parentId) || [];
      children.push(row);
      byParent.set(row.parentId, children);
    });
    const output: WarehousingMasterData[] = [];
    const append = (row: WarehousingMasterData, depth: number): void => {
      output.push({ ...row, name: `${'-- '.repeat(depth)}${row.name}` });
      (row.id ? byParent.get(row.id) || [] : [])
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        .forEach(child => append(child, depth + 1));
    };
    rows
      .filter(row => !row.parentId || !ids.has(row.parentId))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(row => append(row, 0));
    return output;
  }

  private uploadNextImage(files: File[], index: number): void {
    const file = files[index];
    if (!file) {
      this.imageUploadProgress = 0;
      this.imageUploadInProgress = false;
      return;
    }
    const companyId = this.warehousingService.getCompanyId();
    const itemKey = this.itemId ? String(this.itemId) : `draft-${Date.now()}`;
    const path = `warehousing/items/${companyId}/${itemKey}/images`;
    this.firebaseStorageService.uploadItemImageWithThumbnail(file, path).subscribe({
      next: upload => {
        this.imageUploadProgress = upload.progress;
        if (upload.url) {
          this.uploadedImages = [
            ...this.uploadedImages,
            {
              fileName: file.name,
              fileUrl: upload.url,
              thumbnailUrl: upload.thumbnailUrl,
              sortOrder: this.uploadedImages.length,
              primaryImage: this.uploadedImages.length === 0
            }
          ];
          if (upload.thumbnailFailed) {
            this.messageService.add({ severity: 'warn', summary: 'Thumbnail Pending', detail: `${file.name} was uploaded; its directory preview will be added by the backfill job.` });
          }
          this.messageService.add({ severity: 'success', summary: 'Image Uploaded', detail: `${file.name} uploaded.` });
          this.uploadNextImage(files, index + 1);
        }
      },
      error: err => {
        this.imageUploadProgress = 0;
        this.imageUploadInProgress = false;
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: err?.message || `${file.name} could not be uploaded.` });
      }
    });
  }
}
